import { useEffect } from "react";
import { Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
  isRouteErrorResponse } from "react-router";

const ZOHO_WIDGET_SRC =
  "https://salesiq.zohopublic.in/widget?wc=siqffd6fb74406eeaaebae5ae44675f2b79bb1373fb323d35f47cec1aadad80faae";

import * as Sentry from "@sentry/react";

import { authenticate } from "./shopify.server";
import { createTtlCache } from "./utils/cache.server";

// Public config exposed to the browser via window.ENV (set in the document
// body below). Only non-secret values — the client Sentry DSN, environment, and
// release — so entry.client.jsx can initialise browser error reporting.
function publicEnv() {
  return {
    SENTRY_DSN_CLIENT: process.env.SENTRY_DSN_CLIENT || "",
    SENTRY_ENVIRONMENT: process.env.NODE_ENV || "development",
    APP_VERSION: process.env.APP_VERSION || "",
  };
}

// Shop name/email are stable and only used for the support-chat widget, but the
// loader runs on every navigation — cache them briefly to avoid an Admin API
// call per page view (Perf 3.1). Per-instance, 5-minute TTL.
const shopDetailsCache = createTtlCache({ ttlMs: 5 * 60 * 1000, max: 5000 });

/* ---------------- LOADER ---------------- */

export async function loader({ request }) {
  const url = new URL(request.url);

  // Skip auth for OAuth routes and the splash page
  if (url.pathname === "/" || url.pathname.startsWith("/auth") || url.pathname.startsWith("/api/auth")) {
    return { shop: null, apiKey: process.env.SHOPIFY_API_KEY || "", ENV: publicEnv() };
  }

  // No custom "deprecated token" check here: with expiringOfflineAccessTokens
  // (shopify.server.js) an offline session with a past `expires` is normal and
  // authenticate.admin refreshes it. The old helper deleted such sessions and
  // redirected to /auth, leaving the app stuck on a blank "null" page.
  const { admin, session } = await authenticate.admin(request);

  // Serve cached shop details when fresh — avoids a GetShopDetails Admin API
  // call on every navigation.
  const cached = shopDetailsCache.get(session.shop);
  if (cached) {
    return { shop: cached, apiKey: process.env.SHOPIFY_API_KEY || "", ENV: publicEnv() };
  }

  const response = await admin.graphql(`
    query GetShopDetails {
      shop {
        id
        name
        email
        myshopifyDomain
        url
      }
    }
  `);

  const json = await response.json();
  const shop = json.data?.shop ?? null;
  if (shop) shopDetailsCache.set(session.shop, shop);

  return {
    shop,
    apiKey: process.env.SHOPIFY_API_KEY || "",
    ENV: publicEnv(),
  };
}

/* ---------------- APP ---------------- */

export default function App() {
  const { shop, apiKey, ENV } = useLoaderData() || {};

  const name = shop?.name || "";
  const email = shop?.email || "";

  // Load the Zoho SalesIQ chat widget client-side. <script> tags rendered as JSX
  // children do NOT execute in React, so the widget must be injected imperatively
  // here: set the $zoho config (the `ready` handler) BEFORE the widget script so
  // visitor info is registered, then append the widget <script> via createElement
  // (which the browser actually runs). Gated on `shop`; idempotent.
  useEffect(() => {
    if (!shop) return;
    if (document.getElementById("zsiqscript")) return; // already loaded

    window.$zoho = window.$zoho || {};
    window.$zoho.salesiq = window.$zoho.salesiq || {
      ready: function () {
        window.$zoho.salesiq.visitor.email(email);
        window.$zoho.salesiq.visitor.name(name);
        window.$zoho.salesiq.visitor.info({
          name,
          email,
          app: "Codebeans Bulk Image Upload",
          platform: "Shopify",
          environment: "production",
        });
      },
    };

    const s = document.createElement("script");
    s.id = "zsiqscript";
    s.src = ZOHO_WIDGET_SRC;
    s.defer = true;
    document.body.appendChild(s);
  }, [shop, name, email]);

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <meta
          httpEquiv="Permissions-Policy"
          content="camera=(), microphone=(), geolocation=()"
        />

        <link rel="preconnect" href="https://cdn.shopify.com/" />
        <link
          rel="stylesheet"
          href="https://cdn.shopify.com/static/fonts/inter/v4/styles.css"
        />

        <meta name="shopify-api-key" content={apiKey} />
        <script src="https://cdn.shopify.com/shopifycloud/app-bridge.js"></script>
        <Meta />
        <Links />
      </head>

      <body>
        <div style={{ paddingBottom: "2rem" }}>
          <Outlet />
          <ScrollRestoration />
          {/* Public env for the browser (client Sentry DSN/release). Must render
              before <Scripts /> so window.ENV exists when entry.client hydrates. */}
          <script
            dangerouslySetInnerHTML={{
              __html: `window.ENV = ${JSON.stringify(ENV || {})}`,
            }}
          />
          <Scripts />

          {/* Zoho SalesIQ chat is loaded imperatively in the useEffect above —
              React does not execute <script> tags rendered as JSX children. */}
        </div>
      </body>
    </html>
  );
}

/* ---------------- ROOT ERROR BOUNDARY ---------------- */
// Infrastructure-only fallback. When the root errors, the default App layout
// does NOT render — so this boundary renders its own complete HTML document
// (with the same document-level assets) to avoid a blank, script-less page.
// React Router primitives only: boundary.error() can't be used here because it
// depends on the App Bridge context, which is mounted below root (in app.jsx).
// No loader data is available here, so the api key is read from process.env
// (server-side) exactly as the loader above does.
export function ErrorBoundary() {
  const error = useRouteError();
  const apiKey = process.env.SHOPIFY_API_KEY ?? "";

  // Report genuine (non-HTTP-response) render/loader errors to Bugsink. Route
  // error responses (404/redirects/reauth control flow) are not real bugs and
  // are handled server-side by entry.server's handleError, so skip them here.
  if (!isRouteErrorResponse(error)) {
    Sentry.captureException(error);
  }

  const title = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : "Something went wrong";

  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <meta
          httpEquiv="Permissions-Policy"
          content="camera=(), microphone=(), geolocation=()"
        />

        <link rel="preconnect" href="https://cdn.shopify.com/" />
        <link
          rel="stylesheet"
          href="https://cdn.shopify.com/static/fonts/inter/v4/styles.css"
        />

        <meta name="shopify-api-key" content={apiKey} />
        <script src="https://cdn.shopify.com/shopifycloud/app-bridge.js"></script>
        <Meta />
        <Links />
      </head>

      <body>
        <div style={{ paddingBottom: "2rem" }}>
          <h1>{title}</h1>
          <p>Something went wrong. Please try again.</p>
          <ScrollRestoration />
          <Scripts />
        </div>
      </body>
    </html>
  );
}
